import { useState, type ReactNode } from 'react'
import {
  Card,
  Group,
  Stack,
  Text,
  Badge,
  Button,
  Alert,
  ThemeIcon,
  Title,
  Code,
} from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { Link } from 'react-router-dom'
import {
  IconBrandWhatsapp,
  IconServer,
  IconServerBolt,
  IconPlugConnected,
  IconHeartbeat,
  IconExternalLink,
} from '@tabler/icons-react'
import {
  integrationsApi,
  type IntegrationId,
  type IntegrationSecret,
  type HealthResult,
} from '../../../api/integrations'

const INTEGRATION_ICONS: Record<IntegrationId, ReactNode> = {
  whatsapi: <IconBrandWhatsapp size={24} />,
  jira: <IconPlugConnected size={24} />,
  zabbix: <IconServer size={24} />,
  ldap: <IconServerBolt size={24} />,
}

const INTEGRATION_NAMES: Record<IntegrationId, string> = {
  whatsapi: 'WhatsAPI',
  jira: 'Jira',
  zabbix: 'Zabbix',
  ldap: 'LDAP',
}

export function SecretRow({ secret }: { secret: IntegrationSecret }) {
  const consoleUrl = secret.console_url ?? '#'

  if (secret.configured) {
    return (
      <Group justify="space-between" wrap="nowrap" gap="md">
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Text fw={600}>Secret configurado</Text>
          {secret.secret_arn && <Code>{secret.secret_arn}</Code>}
        </Stack>
        <Button
          component="a"
          href={consoleUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="subtle"
          rightSection={<IconExternalLink size={14} />}
        >
          Abrir no AWS
        </Button>
      </Group>
    )
  }

  if (secret.error_message) {
    return (
      <Group justify="space-between" wrap="nowrap" gap="md">
        <Text c="red">Erro: {secret.error_message}</Text>
        <Button
          component="a"
          href={consoleUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="subtle"
          color="red"
          rightSection={<IconExternalLink size={14} />}
        >
          Verificar no AWS
        </Button>
      </Group>
    )
  }

  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Group gap="xs">
        <Text>Ausente:</Text>
        <Code>{secret.secret_arn ?? '—'}</Code>
      </Group>
      <Button
        component="a"
        href={consoleUrl}
        target="_blank"
        rel="noopener noreferrer"
        variant="subtle"
        rightSection={<IconExternalLink size={14} />}
      >
        Criar no AWS
      </Button>
    </Group>
  )
}

export function PerTenantRow({ integration }: { integration: IntegrationSecret }) {
  const count = integration.tenants_count ?? 0
  const url = integration.per_tenant_url ?? '/admin/tenants'
  const name = INTEGRATION_NAMES[integration.integration]

  if (count > 0) {
    return (
      <Group justify="space-between" wrap="nowrap" gap="md">
        <Text c="dimmed">{count} tenant(s) com {name} ativo</Text>
        <Button component={Link} to={url} variant="light">
          Ver tenants
        </Button>
      </Group>
    )
  }

  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text c="red">Nenhum tenant configurado</Text>
      <Button component={Link} to={url} variant="light" color="orange">
        Configurar
      </Button>
    </Group>
  )
}

export function IntegrationCard({ integration }: { integration: IntegrationSecret }) {
  const [healthResult, setHealthResult] = useState<HealthResult | null>(null)

  const healthMutation = useMutation({
    mutationFn: () => integrationsApi.checkHealth(integration.integration),
    onSuccess: (result: HealthResult) => setHealthResult(result),
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Falha ao verificar saúde',
        message: `Não foi possível executar health-check para ${INTEGRATION_NAMES[integration.integration]}`,
      })
    },
  })

  const name = INTEGRATION_NAMES[integration.integration]
  const isPerTenant = integration.scope === 'per_tenant'
  const statusColor = integration.configured ? 'teal' : 'orange'
  const statusLabel = integration.configured ? 'Configurado' : 'Pendente'

  const alertColor =
    healthResult?.status === 'ok'
      ? 'teal'
      : healthResult?.status === 'degraded'
        ? 'yellow'
        : 'red'

  return (
    <Stack gap="sm">
      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon variant="light" size="lg" aria-label={name}>
                {INTEGRATION_ICONS[integration.integration]}
              </ThemeIcon>
              <Title order={4}>{name}</Title>
              {isPerTenant && (
                <Badge variant="outline" color="blue">
                  per-tenant
                </Badge>
              )}
            </Group>
            <Badge color={statusColor}>{statusLabel}</Badge>
          </Group>

          {isPerTenant ? (
            <PerTenantRow integration={integration} />
          ) : (
            <SecretRow secret={integration} />
          )}

          <Group justify="flex-end">
            <Button
              variant="light"
              leftSection={<IconHeartbeat size={16} />}
              loading={healthMutation.isPending}
              onClick={() => healthMutation.mutate()}
            >
              Testar conexão
            </Button>
          </Group>
        </Stack>
      </Card>

      {healthResult && (
        <Alert color={alertColor} title={`Status: ${healthResult.status}`}>
          <Stack gap={4}>
            {healthResult.latency_ms != null && (
              <Text size="sm">Latência: {healthResult.latency_ms} ms</Text>
            )}
            <Text size="sm">{healthResult.message}</Text>
            <Text size="xs" c="dimmed">
              Verificado em {new Date(healthResult.checked_at).toLocaleString()}
            </Text>
          </Stack>
        </Alert>
      )}
    </Stack>
  )
}
